import { KButton, KSpan } from "kahuna-base-react-components"
import { useEffect, useMemo, useRef, useState } from "react"
import { RootState } from "../../../store"
import { connect } from "react-redux"
import { fetchAllSummaries, fetchFilterOptions } from "../../../actions/analysisActions"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { AnalysisSummary } from "../../../types"
import { platformColors } from "../../../constants"
import {
  addCommasToNumber,
  formatDateObject,
  getCurrency,
  getSummaries,
  mapListToCamelCase,
  platformLogos
} from "../../../utility"
import { Chart, ChartOptions } from "chart.js"
import { Doughnut } from "react-chartjs-2"
import { DateRangeType } from "kahuna-base-react-components/dist/components/KSelectRange/KSelectRange"
import { lang } from "../../../constants/languages"
import { MultiValue } from "react-select"

interface PlatformChartData {
  label: string
  icon: string
  totalNet: number
  iconLabel?: string
  label2: string
  percentage?: number
}

const defaultPlatforms = [
  { label: "YouTube - Audio Tier", label2: "YouTube - Audio Tier", icon: platformLogos["YouTube - Audio Tier"] },
  { label: "Apple Music", label2: "apl-apple", icon: platformLogos["apl-apple"] },
  { label: "Meta Instagram", label2: "Meta-instagram", icon: platformLogos["Meta-instagram"] },
  { label: "TikTok", label2: "tiktok", icon: platformLogos["tiktok"] },
  { label: "Meta Facebook", label2: "Metafbk-facebook", icon: platformLogos["Metafbk-facebook"] },
  { label: "Deezer", label2: "dzr-deezer", icon: platformLogos["dzr-deezer"] }
]

const PlatformChart = ({
  currency,
  platformOptions,
  dateValues,
  commonData,
  selectedSubOptions
}: {
  currency: string
  platformOptions: KSelectOption[]
  dateValues: DateRangeType
  commonData: AnalysisSummary[]
  selectedSubOptions: KSelectOption | MultiValue<KSelectOption> | undefined
}) => {
  const [chartData, setChartData] = useState<PlatformChartData[]>([] as PlatformChartData[])
  const [data, setData] = useState<AnalysisSummary[]>(commonData)
  const [totalValue, setTotalValue] = useState<number>(0)

  const hoveredIndexRef = useRef<number>(-1)
  //const [viewMore, setViewMore] = useState<boolean>(false)

  useEffect(() => {
    if ((selectedSubOptions as KSelectOption[]).length === 0) {
      setData(commonData)
    }
  }, [commonData])

  const didMountRef = useRef(false)

  useEffect(() => {
    const getAllSummary = async () => {
      if (!didMountRef?.current || (selectedSubOptions as KSelectOption[]).length === 0) {
        didMountRef.current = true
        return
      }
      let responseData
      if (dateValues && dateValues[0] && dateValues[1]) {
        const startDate = formatDateObject(dateValues[0])
        const endDate = formatDateObject(dateValues[1])
        responseData = await getSummaries("date", "platform", "all_platforms", false, startDate, endDate)
        setData(mapListToCamelCase(responseData))
      }
    }

    getAllSummary().then(() => {})
  }, [dateValues])

  useEffect(() => {
    if (data === null || data === undefined) return
    if (data && platformOptions.length > 0) {
      const chartDataArray: PlatformChartData[] = []
      const allTotalNetValue = data.reduce((sum, currentValue) => {
        sum += Number(currentValue.totalNet)
        return sum
      }, 0) // total net value of all platforms, not platform specific
      let percentageSum = 0
      platformOptions.forEach((option, index) => {
        const matched = data.filter((summary) => summary.label === option.value2)
        if (matched.length === 0) return
        const totalNetResult = data
          .filter((summary) => summary.label === option.value2)
          .reduce((sum, currentValue) => {
            sum += Number(currentValue.totalNet)
            return sum
          }, 0)
        const percentage = Number(((totalNetResult / allTotalNetValue) * 100).toFixed(0))
        percentageSum += percentage
        chartDataArray.push({
          label: option.label,
          icon: option.icon || "",
          totalNet: totalNetResult,
          iconLabel: option.iconLabel || option.icon,
          label2: option.value2 || "",
          percentage
        })
      })
      if (chartDataArray.length < 6) {
        const existingLabels = new Set(chartDataArray.map((item) => item.label))

        defaultPlatforms.forEach((defaultPlatform) => {
          if (chartDataArray.length >= 6) return
          if (!existingLabels.has(defaultPlatform.label)) {
            chartDataArray.push({
              label: defaultPlatform.label,
              label2: defaultPlatform.label2,
              icon: defaultPlatform.icon,
              totalNet: 0
            })
          }
        })
      }

      const sortedChartDataArray = chartDataArray.sort((a, b) => b.totalNet - a.totalNet)
      if (sortedChartDataArray[0]?.percentage) {
        const correctionFactor = Number((100 - percentageSum).toFixed(0))
        sortedChartDataArray[0].percentage += correctionFactor
      }
      setChartData(sortedChartDataArray)
      setTotalValue(allTotalNetValue)
    }
  }, [data, platformOptions])

  const myChartData = useMemo(() => {
    return {
      labels: chartData.map((data) => data.label),
      datasets: [
        {
          label: lang.analytics.revenue,
          data: chartData.map((data) => Number(data.totalNet.toFixed(2))),
          backgroundColor: chartData.map((data) => platformColors[data?.label2]),
          hoverBackgroundColor: chartData.map((data) => `${platformColors[data?.label2]}88`), // Disable hover color
          hoverBorderWidth: 0, // Disable hover border width
          yAxisID: "y",
          borderSkipped: false,
          barThickness: 16
        }
      ]
    }
  }, [chartData])

  const chartRef = useRef<Chart<"doughnut"> | null>(null)

  const chartKey = useMemo(() => {
    const dateKey =
      dateValues && dateValues[0] && dateValues[1]
        ? `${dateValues[0].toString()}-${dateValues[1].toString()}`
        : "no-dates"
    const dataKey = chartData.map((d) => `${d.label}:${Number(d.totalNet).toFixed(2)}`).join("|")
    return `${dateKey}__${dataKey}`
  }, [chartData, dateValues])

  const centerTextPlugin = useMemo(() => {
    return {
      id: "centerText",
      afterDatasetsDraw: (chart: any) => {
        let hoveredData = {
          totalNet: totalValue,
          percentage: undefined,
          label: lang.analytics.total
        } as PlatformChartData

        const { width, height, ctx } = chart
        const centerX = width / 2
        const centerY = height / 2

        if (hoveredIndexRef.current !== -1 && chartData[hoveredIndexRef.current]) {
          hoveredData = chartData[hoveredIndexRef.current]
        }

        if (!hoveredData || chartData.length === 0) return

        ctx.save()
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"

        ctx.font = "bold 16px Inter"
        ctx.fillStyle = "#111"
        ctx.fillText(hoveredData.label, centerX, centerY - 20)

        const sign = getCurrency(currency)
        const amount = `${sign}${addCommasToNumber(Number(hoveredData.totalNet).toFixed(2))}`
        ctx.font = "18px Inter"
        ctx.fillStyle = "#111"
        ctx.fillText(amount, centerX, centerY + 5)

        if (hoveredData.percentage) {
          ctx.font = "14px Inter"
          ctx.fillStyle = "#111"
          ctx.fillText(
            `${lang.analytics.percentage_of_total.replace("<param>", `${hoveredData.percentage}%`)}`,
            centerX,
            centerY + 25
          )
        }

        ctx.restore()
      }
    }
  }, [chartData, totalValue, currency])

  const options: ChartOptions<"doughnut"> = {
    onHover: (event, elements, chart) => {
      if (elements.length > 0) {
        const index = elements[0].index
        hoveredIndexRef.current = index
        if (chartRef.current) {
          chartRef.current.update() // Update the chart to apply changes
        }
      } else {
        hoveredIndexRef.current = -1
        if (chartRef.current) {
          chartRef.current.update() // Update the chart to apply changes
        }
      }
    },
    cutout: "70%",
    responsive: true,
    animation: {
      duration: 1000 // default is 1000
    },
    maintainAspectRatio: false,
    indexAxis: "y",
    layout: {
      padding: {
        left: 0
      }
    },
    plugins: {
      legend: {
        display: false,
        labels: {
          color: "#000",
          font: {
            family: "Inter",
            weight: 500,
            size: 13,
            lineHeight: "13px"
          }
        }
      },
      datalabels: {
        display: false
      },
      tooltip: {
        enabled: false,
        displayColors: false,
        caretSize: 0,
        caretPadding: 10,
        xAlign: "center",
        yAlign: "bottom",
        backgroundColor: "transparent",
        bodyFont: {
          family: "Inter",
          weight: 400,
          size: 14,
          lineHeight: "20px"
        },
        callbacks: {
          label: (context) => {
            return ""
          },
          title: (context) => ""
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
          color: "rgba(183, 183, 183, 0.30)",
          drawTicks: false
        },
        border: {
          display: false,
          dash: [3, 3]
        },
        ticks: {
          display: false,
          color: "#999",
          font: {
            family: "Inter",
            weight: 500,
            size: 11,
            lineHeight: "12px"
          }
        }
      },
      y: {
        grid: {
          display: false
        },
        border: {
          display: false,
          color: "#999"
        },
        ticks: {
          display: false,
          color: chartData.map((data, index) => (data.label === "Spotify" ? "transparent" : "#000")),
          crossAlign: "far",
          padding: 10,
          font: {
            family: "Inter",
            size: 14,
            weight: 500,
            lineHeight: "20px"
          }
        }
      }
    },
    elements: {
      bar: {
        borderRadius: {
          bottomLeft: 6,
          bottomRight: 6,
          topLeft: 6,
          topRight: 6
        }
      }
    }
  }

  return (
    <div
      className="w-full p-5 flex flex-col justify-start gap-3 h-full"
      style={{
        borderRadius: "10px",
        border: "1px solid var(--stroke-soft-200, #F3F3F3)",
        background: " var(--bg-white-0, #FFF)",
        boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
      }}
    >
      <div className="flex flex-row justify-between">
        <div className="flex flex-row gap-1 items-center pl-2.5">
          <img src="/analytics_icons/link.svg" alt="link-icon" />
          <KSpan text={lang.analytics.platform_breakdown} color="#000" fontWeight={500} />
        </div>
      </div>
      <div className="grow flex flex-col items-center justify-start w-full">
        <div className="flex pt-5">
          {chartData && chartData.length > 0 && (
            <Doughnut
              key={chartKey}
              ref={chartRef}
              data={myChartData}
              options={options}
              plugins={[centerTextPlugin]}
              style={{
                width: "100%",
                maxWidth: "300px",
                aspectRatio: 1,
                display: "flex",
                justifyContent: "flex-start"
              }}
            />
          )}
        </div>
        <div className="flex-1 w-full flex flex-row flex-wrap">
          {chartData.map((cd: PlatformChartData, index: number) => {
            return (
              <div
                key={`${cd.label}-${index}`}
                className="flex p-2 w-1/2 flex-row items-center justify-between cursor-pointer hover:bg-[#F3F3F3]"
                style={{
                  borderTopWidth: 0,
                  borderLeftWidth: 0,
                  borderRightWidth: index % 2 === 1 ? 0 : 1,
                  borderBottomWidth:
                    chartData.length - 1 === index || (chartData.length - 2 === index && index % 2 === 0) ? 0 : 1
                }}
                onMouseEnter={() => {
                  hoveredIndexRef.current = index
                  if (chartRef.current) {
                    chartRef.current.setActiveElements([{ datasetIndex: 0, index }])
                    chartRef.current.update()
                  }
                }}
                onMouseLeave={() => {
                  hoveredIndexRef.current = -1
                  if (chartRef.current) {
                    chartRef.current.setActiveElements([])
                    chartRef.current.update()
                  }
                }}
              >
                <div className="flex flex-row items-center">
                  <div
                    className="w-1.5 h-1.5 rounded-[10px] shrink-0"
                    style={{
                      backgroundColor: platformColors[cd?.label2] || "#333"
                    }}
                  />
                  <img
                    src={platformLogos[cd?.label2] || cd.icon}
                    width={cd.label === "Spotify" ? 80 : 22}
                    height={cd.label === "Spotify" ? 24.0135478 : 22}
                    className="ml-2 mr-2"
                  />
                  <KSpan text={cd.label === "Spotify" ? "" : cd.label} color="#111" fontSize={12} />
                </div>
                <KSpan text={`${cd?.percentage ? String(cd?.percentage) : "0"}%`} color="#111" fontSize={12} />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    filterOptions: state.analysis.filterOptions,
    allSummaries: state.analysis.allSummaries,
    currency: state.auth.user.preferredCurrency
  }
}

export default connect(mapStateToProps, { fetchFilterOptions, fetchAllSummaries })(PlatformChart)
